import type { Command } from 'commander';
import { Logger } from '@gitlab-org/logging';
import {
  parseSpecifier,
  PluginUpdater,
  UNVERSIONED,
} from '@gitlab-org/duo-plugin-marketplace/node';
import type { DuoCommand } from '../duo_command';
import { ExitHandler } from '../../utils/exit';
import type { PluginContainerProvider } from './plugin_services';

export class PluginUpdateCommand implements DuoCommand {
  readonly name = 'update <plugin@marketplace>';

  readonly description =
    'Update an installed plugin to the latest version across all scopes where it is installed.';

  readonly #containerProvider: PluginContainerProvider;

  constructor(containerProvider: PluginContainerProvider) {
    this.#containerProvider = containerProvider;
  }

  register(node: Command): void {
    node.action(async (specifier: string) => {
      try {
        const spec = parseSpecifier(specifier);
        const updater = this.#containerProvider.getRequiredService(PluginUpdater, node);
        const result = await updater.update(spec);
        if (!result.updated) {
          // eslint-disable-next-line no-console
          console.log(`Plugin ${result.id} is already at the latest version (${result.version})`);
        } else if (result.version === UNVERSIONED) {
          // eslint-disable-next-line no-console
          console.log(`Refreshed ${result.id} from source`);
        } else {
          const previous = result.previousVersions.filter((version) => version !== result.version);
          const transition =
            previous.length > 0
              ? `${previous.join(', ')} -> ${result.version}`
              : `to ${result.version}`;
          // eslint-disable-next-line no-console
          console.log(`Updated ${result.id} ${transition}`);
        }
      } catch (e) {
        const logger = this.#containerProvider.getRequiredService(Logger, node);
        logger.error('Failed to update plugin', e instanceof Error ? e : new Error(String(e)));
        const message = e instanceof Error ? e.message : String(e);
        // eslint-disable-next-line no-console
        console.error(`Failed to update plugin: ${message}`);
        await this.#containerProvider.getRequiredService(ExitHandler, node).exit(1);
      }
    });
  }
}
